import { dxtToLocalServerTime } from "@dexteel/mesf-core";

import { MenuOptionType } from "@dexteel/mesf-core";
import { Build, CallSplit, Comment } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { useState } from "react";
import { DelayByShift } from "../models/DelayByShift";

type Props = {
	setSelectedData: (delay: DelayByShift) => void;
	setShowModal: (
		show: "new" | "edit" | "delete" | "justify" | "split" | "",
	) => void;
	setShowAdjustmentModal: (show: boolean) => void;
	updateUrl: (
		newIncludeMicroDelays?: boolean,
		newOnlyUndeclared?: boolean,
		newAction?: string | null,
		newDelayId?: string | null,
	) => void;
};

export const useDelaysOptionFunctions = ({
	setSelectedData,
	setShowModal,
	setShowAdjustmentModal,
	updateUrl,
}: Props) => {
	const [messageDeleteModal, setMessageDeleteModal] = useState("");

	// Create
	const createData = () => {
		if (updateUrl) {
			updateUrl(undefined, undefined, "new", null);
		} else {
			setShowModal("new");
		}
	};

	// Edit
	const editData = (data: DelayByShift) => {
		updateUrl(undefined, undefined, "edit", data.DelayId?.toString());
		setSelectedData(data);
	};

	// Justify
	const justifyData = (data: DelayByShift) => {
		updateUrl(undefined, undefined, "justify", data.DelayId?.toString());
		setSelectedData(data);
	};

	// Adjustment
	const adjustment = (data: DelayByShift) => {
		updateUrl(undefined, undefined, null, data.DelayId?.toString());
		setSelectedData(data);
		setShowAdjustmentModal(true);
	};

	// Split
	const splitData = (data: DelayByShift) => {
		updateUrl(undefined, undefined, "split", data.DelayId?.toString());
		setSelectedData(data);
	};

	// Delete
	const deleteData = (data: DelayByShift) => {
		updateUrl(undefined, undefined, "delete", data.DelayId?.toString());
		setSelectedData(data);
		const dateOpen = dxtToLocalServerTime(data.Start, "HH:mm:ss");
		setMessageDeleteModal(
			`The delay at ${dateOpen} will be deleted. Do you want to continue?`,
		);
	};

	// Get Menu options
	const getMenuOptions = (data: DelayByShift) => {
		let options: MenuOptionType[] = [];

		if (data) {
			options = options.concat([
				{
					name: "Justify Delay",
					key: "justify_delay",
					onClick: () => justifyData(data),
					icon: <Comment />,
				},
				{
					name: "Edit Delay",
					key: "edit_delay",
					onClick: () => editData(data),
					icon: <Edit />,
					disabled: !data.IsDelayManual,
				},
				{
					name: "Delete Delay",
					key: "delete_delay",
					onClick: () => deleteData(data),
					icon: <DeleteIcon />,
					disabled: !data.IsDelayManual,
				},
				{
					name: "Split Delay",
					key: "split_delay",
					onClick: () => splitData(data),
					icon: <CallSplit />,
				},
				{
					name: "Adjustment",
					key: "adjustment_delay",
					onClick: () => adjustment(data),
					icon: <Build />,
					disabled: data.AssetId !== 12,
				},
			]);
		}
		options.push({
			name: "New Delay",
			key: "create_new_delay",
			onClick: () => createData(),
			icon: <PlaylistAddIcon />,
		});

		return options;
	};

	return {
		createData,
		justifyData,
		splitData,
		adjustment,
		getMenuOptions,
		messageDeleteModal,
		setMessageDeleteModal,
	};
};
